'use client';

import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';

interface LiveAnnouncerProps {
  message: string;
  politeness?: 'polite' | 'assertive' | 'off';
  clearOnUnmount?: boolean;
}

/**
 * ARIA live region for announcing dynamic content changes to screen readers
 *
 * @example
 * ```tsx
 * const [announcement, setAnnouncement] = useState('');
 *
 * // When data loads
 * setAnnouncement('10 new listings loaded');
 *
 * <LiveAnnouncer message={announcement} politeness="polite" />
 * ```
 */
export function LiveAnnouncer({
  message,
  politeness = 'polite',
  clearOnUnmount = true,
}: LiveAnnouncerProps) {
  const messageRef = useRef<string>('');

  useEffect(() => {
    if (message && message !== messageRef.current) {
      messageRef.current = message;
    }
  }, [message]);

  useEffect(() => {
    return () => {
      if (clearOnUnmount) {
        messageRef.current = '';
      }
    };
  }, [clearOnUnmount]);

  return (
    <Box
      role="status"
      aria-live={politeness}
      aria-atomic="true"
      sx={{
        position: 'absolute',
        left: '-10000px',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      }}
    >
      {message}
    </Box>
  );
}

/**
 * Hook for announcing messages to screen readers
 */
export function useAnnouncer() {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const announce = (message: string, delay: number = 100) => {
    // Clear any pending announcement
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Delay announcement slightly to ensure it's picked up by screen readers
    timeoutRef.current = setTimeout(() => {
      const announcer = document.getElementById('global-announcer');
      if (announcer) {
        announcer.textContent = message;
        // Clear after announcement
        setTimeout(() => {
          if (announcer) announcer.textContent = '';
        }, 1000);
      }
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { announce };
}

/**
 * Global announcer that should be included once in the app
 */
export function GlobalAnnouncer() {
  return (
    <Box
      id="global-announcer"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      sx={{
        position: 'absolute',
        left: '-10000px',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      }}
    />
  );
}
